import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SITE } from "@/constants/testIds";

const CONCERN_OPTIONS = [
  "Age-related health",
  "Hormonal balance",
  "Body composition",
  "Skin health",
  "Not sure yet",
];

export const CallbackDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", phone: "", concern: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/callback-requests", form);
      setDone(true);
      toast.success("We'll call you back shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try calling us directly.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setDone(false);
        setForm({ name: "", phone: "", concern: "", message: "" });
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={SITE.callbackDialog}
        className="bg-cream border-line max-w-md p-0 overflow-hidden"
      >
        {done ? (
          <div
            data-testid={SITE.callbackSuccess}
            className="p-10 text-center"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-sage/10 flex items-center justify-center">
              <Check className="w-7 h-7 text-sage" strokeWidth={1.75} />
            </div>
            <h3 className="font-serif text-3xl text-ink mt-6">Thank you.</h3>
            <p className="text-ink-muted mt-3 leading-relaxed">
              Our team will reach out within the next business hour. For urgent
              queries, please call <span className="text-ink">+91 9319757979</span>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="p-8 pb-2">
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-ink tracking-tight">
                  Request a callback
                </DialogTitle>
                <DialogDescription className="text-ink-muted mt-2">
                  Share a few details and our team will call you back.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-8 pb-8 pt-4 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                  Your name
                </Label>
                <Input
                  id="name"
                  data-testid={SITE.callbackName}
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="bg-white border-line focus-visible:ring-sage h-11"
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                  Phone
                </Label>
                <Input
                  id="phone"
                  data-testid={SITE.callbackPhone}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="bg-white border-line focus-visible:ring-sage h-11"
                  placeholder="+91 9XXXXXXXXX"
                  type="tel"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                  Primary concern
                </Label>
                <div className="flex flex-wrap gap-2" data-testid={SITE.callbackConcern}>
                  {CONCERN_OPTIONS.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => update("concern", c)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                        form.concern === c
                          ? "bg-sage text-cream border-sage"
                          : "bg-white text-ink border-line hover:border-sage/50"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                  Anything else? <span className="normal-case tracking-normal">(optional)</span>
                </Label>
                <Textarea
                  id="message"
                  data-testid={SITE.callbackMessage}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={3}
                  className="bg-white border-line focus-visible:ring-sage resize-none"
                  placeholder="A note for the doctor"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid={SITE.callbackSubmit}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-ink hover:bg-sage-dark text-cream px-5 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-60"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending…" : "Request callback"}
              </button>
              <p className="text-[11px] text-ink-muted text-center pt-1">
                By submitting, you consent to be contacted by ReGain Clinic.
              </p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CallbackDialog;
